import { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Random integer generator function
 */
function randomIntFromInterval(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

/**
 * Compilations section animation hook.
 * Creates dual-row horizontal scroll animation with pin effect to ensure users see all images.
 */
export const useCompilationsAnimations = (topRowImages, bottomRowImages) => {
  useEffect(() => {
    const compilationsSection = document.querySelector('.compilations-section');
    if (!compilationsSection || !topRowImages.length || !bottomRowImages.length) return;

    const compilationsInner = compilationsSection.querySelector('.compilations-inner');
    const topRow = compilationsSection.querySelector('.row--top');
    const bottomRow = compilationsSection.querySelector('.row--bottom');

    if (!compilationsInner || !topRow || !bottomRow) return;

    const topSections = gsap.utils.toArray('.row--top .compilations-panel');
    const bottomSections = gsap.utils.toArray('.row--bottom .compilations-panel');

    // Reset any existing transforms
    gsap.set(compilationsSection, { clearProps: "all" });
    gsap.set(compilationsInner, { clearProps: "all" });
    topSections.forEach(section => gsap.set(section, { clearProps: "all" }));
    bottomSections.forEach(section => gsap.set(section, { clearProps: "all" }));

    // Calculate total scroll distance needed
    const topRowTotalWidth = topRow.scrollWidth;
    const bottomRowTotalWidth = bottomRow.scrollWidth;
    const maxScrollDistance = Math.max(topRowTotalWidth, bottomRowTotalWidth);
    
    // Add extra distance so the last image can scroll to the center of viewport
    const scrollHeight = maxScrollDistance + (window.innerWidth / 2);

    // Pre-calculate random values for each section to prevent jitter
    const topRowAnimations = topSections.map((section, index) => {
      const rndSTART = randomIntFromInterval(10, 14);
      const startMultiplier = rndSTART / 10;
      const startX = window.innerWidth / startMultiplier;
      const endX = -1 * (topRowTotalWidth - window.innerWidth);

      return {
        section,
        startX,
        endX,
        rndVH: randomIntFromInterval(5, 20)
      };
    });

    const bottomRowAnimations = bottomSections.map((section, index) => {
      const rndSTART = randomIntFromInterval(10, 12);
      const startMultiplier = rndSTART / 10;
      const startX = -1 * (bottomRowTotalWidth * startMultiplier);
      const endX = bottomRowTotalWidth - (window.innerWidth / 2);

      return {
        section,
        startX,
        endX,
        rndVH: randomIntFromInterval(5, 20)
      };
    });

    // Create a single ScrollTrigger with pin for the entire section
    ScrollTrigger.create({
      trigger: compilationsSection,
      start: "top top",
      end: `+=${scrollHeight}px`,
      pin: true,
      scrub: 1,
      markers: false,
      onUpdate: (self) => {
        const progress = self.progress;

        // Top row animations (right to left)
        topRowAnimations.forEach((anim) => {
          // Calculate horizontal position based on progress
          const currentX = anim.startX + (anim.endX - anim.startX) * progress;
          gsap.set(anim.section, { x: currentX });

          // Opacity animation (fade in during first 30% of scroll)
          if (progress <= 0.3) {
            const opacityProgress = progress / 0.3;
            gsap.set(anim.section, { opacity: opacityProgress });
          } else {
            gsap.set(anim.section, { opacity: 1 });
          }
        });

        // Bottom row animations (left to right)
        bottomRowAnimations.forEach((anim) => {
          // Calculate horizontal position based on progress
          const currentX = anim.startX + (anim.endX - anim.startX) * progress;
          gsap.set(anim.section, { x: currentX });

          // Opacity animation (fade in during first 30% of scroll)
          if (progress <= 0.3) {
            const opacityProgress = progress / 0.3;
            gsap.set(anim.section, { opacity: opacityProgress });
          } else {
            gsap.set(anim.section, { opacity: 1 });
          }
        });
      }
    });

    return () => {
      // Cleanup all ScrollTriggers
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === compilationsSection) {
          trigger.kill();
        }
      });

      // Reset transforms
      gsap.set(compilationsSection, { clearProps: "all" });
      gsap.set(compilationsInner, { clearProps: "all" });
      topSections.forEach(section => gsap.set(section, { clearProps: "all" }));
      bottomSections.forEach(section => gsap.set(section, { clearProps: "all" }));
    };
  }, [topRowImages, bottomRowImages]);
};
